import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import clsx from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { MessageActions } from "./MessageActions";
import { Avatar } from "./ui/Avatar";
import { useIsDesktop } from "../lib/media";
import {
  AttachmentPreview,
  canPreview,
  isImageType,
  type PreviewFile,
} from "./AttachmentPreview";
import type { IntentKind } from "../lib/quoting";
import type {
  EmailAddress,
  Message,
  MessageAttachment,
  MessageLocation,
} from "../types";
import { absoluteTime, dayAndTime, relativeTime } from "../lib/time";

/**
 * How long an agent may correct their own note.
 *
 * Mirrors NOTE_EDIT_WINDOW_MS on the server, which is the authority -- this copy
 * only decides whether to offer the button. They are allowed to disagree: a
 * clock a minute fast here means an Edit button that refuses on save, with the
 * server's reason shown, which is the right way round.
 */
const EDIT_WINDOW_MS = 5 * 60 * 1000;

/**
 * The line `@helpdesk/whatsapp` writes for a message that is only a sticker.
 *
 * Every WhatsApp message renders to text, because a ticket whose newest message
 * is blank cannot be told from a bug -- so a sticker becomes "[sticker]", and
 * that is what the ticket list, search and the ticket's own subject show. Once
 * the bubble can draw the sticker itself the line has nothing left to say, so
 * it is dropped here and only here.
 *
 * Matched exactly, not by shape. Every placeholder that package writes is in
 * square brackets, and treating "[document: invoice.pdf]" or a customer who
 * typed "[sticker]" at us the same way would be hiding words somebody sent.
 */
const STICKER_PLACEHOLDER = "[sticker]";

/**
 * The DOM id a link to one message points at.
 *
 * Exported because three places have to agree on it: the bubble that carries
 * it, the menu item that copies it, and the ticket view that scrolls to it on
 * arrival. Deriving it separately in each was how the link worked from the
 * clipboard and did nothing when pasted back.
 */
export function messageAnchor(messageId: number): string {
  return `message-${messageId}`;
}

/**
 * Who a forward went to, as one readable phrase.
 *
 * Names where the record has them and addresses where it does not -- somebody
 * forwarding to a supplier usually typed a bare address, and "to
 * accounts@acme.com" is more use than "to 1 person".
 *
 * Truncated to two, because the badge sits in a wrapping header beside the
 * author, the delivery state and the time: a forward to nine people would push
 * everything else onto its own line and turn a dense thread into a column of
 * addresses. The full list is in the message's own To line below.
 */
function forwardedTo(message: Message): string {
  const people = message.toRecipients ?? [];
  if (people.length === 0) return "";

  const named = people.map((person) => person.name || person.email);
  if (named.length <= 2) return named.join(" and ");

  return `${named.slice(0, 2).join(", ")} and ${named.length - 2} more`;
}

/**
 * One entry in the conversation.
 *
 * Three visual treatments, because confusing them has real consequences:
 * inbound (the customer), outbound (what we sent them), and internal notes,
 * which must never look like something the customer can see.
 *
 * Deliberately not banded by position, unlike the ticket list. The white /
 * grey here already means something -- which way the message went -- and
 * striping over it would make an internal note's colour a matter of where it
 * happened to land in the thread.
 */
export function MessageBubble({
  message,
  ticketId,
  currentUserId,
  highlighted = false,
  onAct,
  onCopyLink,
  collapsed = false,
  onToggleCollapsed,
}: {
  message: Message;
  ticketId: number;
  currentUserId: number;
  /** Arrived here from a link to this message; say so, briefly. */
  highlighted?: boolean;
  onAct: (kind: IntentKind, message: Message) => void;
  onCopyLink: (messageId: number) => Promise<boolean>;
  /**
   * Folded to the sender and one line of what they said, as Gmail's app shows
   * every message in a thread but the newest. Only the phone layout asks for
   * it; the desktop never passes either of these and draws what it always did.
   */
  collapsed?: boolean;
  /** Present when the sender line should fold and unfold the message. */
  onToggleCollapsed?: () => void;
}) {
  const [showQuoted, setShowQuoted] = useState(false);
  /* The sender's addresses on a phone, folded behind "to …" as Gmail does:
     a narrow screen has no room for a From, To and Cc line on every message,
     and the question they answer is asked of one message in twenty. */
  const [showDetails, setShowDetails] = useState(false);
  const isDesktop = useIsDesktop();
  const body = useRef<HTMLDivElement>(null);
  const quotedBody = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState<PreviewFile | null>(null);

  const isNote = message.kind === "note";
  const isOutbound = message.kind === "outbound";

  /*
   * `fromPhone` before "Unknown", and after both names.
   *
   * A WhatsApp message from somebody who has not set a profile name has no name
   * at all and no address either, so without the number it would render as
   * "Unknown" -- which is wrong twice over: the desk knows exactly who sent it,
   * and the number is what an agent recognises.
   */
  const author =
    message.authorName ??
    message.fromName ??
    message.fromEmail ??
    message.fromPhone ??
    "Unknown";

  const { visible, quoted } = splitQuotedText(message.bodyText);

  /*
   * Inline images are drawn only when nothing else is drawing them, and the
   * API now says which those are rather than leaving it to be guessed from the
   * presence of an HTML body.
   *
   * An email's embedded pictures -- the signature logo, a screenshot pasted
   * into the paragraph about it -- are pointed at the inline route as the mail
   * is read and drawn by the body itself, so they are absent from this list.
   * What is left is the WhatsApp sticker, which has no HTML body to sit in and
   * is the whole reason this exists.
   */
  const inlineImages = message.inlineImages;

  /*
   * Whether the text body still says anything the bubble is not about to show.
   *
   * A location's placeholder is the coordinates the card below renders in full,
   * and a sticker's is a word for a picture that is now on the screen. Both are
   * for the places that only get text.
   */
  const bodySpokenFor =
    message.location !== null ||
    (inlineImages.length > 0 && message.bodyText?.trim() === STICKER_PLACEHOLDER);

  /*
   * Whether the Edit button is offered at all. Re-evaluated on a timer so the
   * button disappears while somebody is looking at it rather than lingering to
   * be clicked and refused -- a control that is there but does not work is
   * worse than one that was never there.
   */
  const [now, setNow] = useState(() => Date.now());
  const mine = isNote && message.authorUserId === currentUserId;
  const age = now - new Date(message.sentAt).getTime();
  const canEdit = mine && age < EDIT_WINDOW_MS;

  useEffect(() => {
    if (!mine || age >= EDIT_WINDOW_MS) return;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      EDIT_WINDOW_MS - age,
    );
    return () => window.clearTimeout(timer);
  }, [mine, age]);

  /*
   * Sending a failed reply again.
   *
   * Nothing is patched optimistically. The server broadcasts `message.updated`
   * the moment the row goes back to `queued`, so the badge changes for everyone
   * looking at the ticket rather than only for whoever pressed it -- which
   * matters here, because two agents watching a red badge is exactly the
   * situation this button creates. Refetching the ticket keeps a client whose
   * socket has dropped honest.
   */
  const retryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.retrySend(message.id),
    onSettled: () => {
      void retryClient.invalidateQueries({ queryKey: queryKeys.ticket(ticketId) });
    },
  });
  const retryError = retry.error instanceof Error ? retry.error.message : null;

  useFitToWidth(body, !isDesktop && !collapsed, message.bodyHtml);
  useFitToWidth(
    quotedBody,
    !isDesktop && !collapsed && showQuoted,
    message.quotedHtml,
  );

  if (collapsed) {
    return (
      <CollapsedMessage
        message={message}
        author={author}
        summary={summaryLine(message, visible)}
        highlighted={highlighted}
        onExpand={onToggleCollapsed}
      />
    );
  }

  return (
    <article
      id={messageAnchor(message.id)}
      /* `scroll-mt-4` keeps a little air above a message scrolled to from a
         link, so it does not land flush against the top of the pane and read
         as cut off. */
      className={clsx(
        /* Edge to edge on a phone, with a rule between messages rather than a
           card round each: the card's border and margin cost 40px of a 390px
           line, and Gmail's app reads full width for the same reason. */
        "scroll-mt-4 border-b px-4 py-3 md:rounded-lg md:border",
        isNote
          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
          : isOutbound
            ? "border-line bg-surface-2/60"
            : "border-line bg-surface",
        /* A ring rather than a changed background: the background already
           says which way the message went, and overwriting that to say
           "you followed a link here" trades a permanent fact for a
           momentary one. */
        highlighted &&
          "ring-2 ring-brand-500 max-md:ring-inset md:ring-offset-2 md:ring-offset-app",
      )}
    >
      <header className="mb-2 flex flex-wrap items-baseline gap-2 max-md:items-center">
        {/*
          On a phone the sender line leads with a face and, when the thread
          allows it, folds the message away again on a tap -- the same target
          that unfolded it, so the gesture undoes itself.
        */}
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded
            className="flex min-w-0 items-center gap-2.5 text-left max-md:order-[-2]"
          >
            <SenderAvatar message={message} author={author} className="md:hidden" />
            <span className="truncate text-sm font-medium max-md:text-[15px] max-md:font-semibold">
              {author}
            </span>
          </button>
        ) : (
          <span className="flex min-w-0 items-center gap-2.5 max-md:order-[-2]">
            <SenderAvatar message={message} author={author} className="md:hidden" />
            <span className="text-sm font-medium max-md:text-[15px] max-md:font-semibold">
              {author}
            </span>
          </span>
        )}

        {isNote ? (
          <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-800 dark:text-amber-100">
            Internal note
          </span>
        ) : isOutbound ? (
          <span className="text-[11px] text-faint">
            {message.isForward ? "forwarded" : "sent"}
          </span>
        ) : null}

        {/*
          A forward, named as one, with the people it went to.
          
          Three bubble styles already carry the distinction that matters most
          -- inbound, outbound, internal note -- and a fourth would dilute
          them, so this is a badge rather than a colour. But it is a badge and
          not just the changed word beside the author, because forwarding is
          the one outbound action whose *recipient* is the whole point: a
          reply goes to the customer and everybody knows it, while a forward
          went to somebody who is not in this conversation and whose name
          nobody can guess from the thread.

          The names are in the badge rather than a tooltip for the same
          reason. "Who did we send this to" is the first question asked of a
          forward, and an answer you have to hover to find is one that gets
          missed in the fortnight before somebody asks why a customer's
          details went to a third party.
        */}
        {message.isForward ? (
          <span
            className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-950 dark:text-violet-300"
            title={
              message.subject
                ? `Sent as “${message.subject}”`
                : undefined
            }
          >
            Forwarded{forwardedTo(message) ? ` to ${forwardedTo(message)}` : ""}
          </span>
        ) : null}

        {/*
          Delivery state for outbound mail. A reply stuck in "queued" or gone
          "failed" is something the agent must notice -- silently swallowing it
          means a customer never gets an answer somebody believed was sent.
        */}
        {message.delivery === "queued" ? (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            Sending…
          </span>
        ) : message.delivery === "failed" ? (
          <>
            <span
              title={message.deliveryError ?? undefined}
              className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              Failed to send
            </span>
            {/*
              Beside the badge rather than in the ⋯ menu. A failed reply is a
              customer who has not been answered, and the thing to do about it
              should be where the bad news is -- a menu is where an action goes
              when finding it is not urgent.
            */}
            <button
              type="button"
              onClick={() => retry.mutate()}
              disabled={retry.isPending}
              className="rounded border border-red-300 px-1.5 py-0.5 text-[10px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
            >
              {retry.isPending ? "Sending…" : "Try again"}
            </button>
            {/*
              Said out loud, not just left in a tooltip. Most of these fail for
              a reason that will repeat -- a closed WhatsApp window, an address
              Gmail will not send from -- and an agent who presses this three
              times before reading why has lost the minutes in which they could
              have rung instead.
            */}
            {retryError ? (
              <span className="text-[11px] text-red-700 dark:text-red-300">
                {retryError}
              </span>
            ) : null}
          </>
        ) : null}

        {/*
          Marked when the words have changed since they were written. A
          colleague may have read the original and acted on it, so a silent
          rewrite is how two people end up working from different notes.
        */}
        {message.editedAt ? (
          <span
            title={`Edited ${absoluteTime(message.editedAt)}`}
            className="text-[11px] italic text-faint"
          >
            edited
          </span>
        ) : null}

        {/* The time and the menu travel together. As loose siblings of a
            wrapping header they came apart in a narrow reading pane, leaving
            a ⋯ stranded on a line of its own under the author's name. */}
        {/* On a phone pulled up beside the sender, so the badges are what
            wraps onto a second line -- not the time, which is what a reader
            scanning the thread is looking for. */}
        <span className="ml-auto flex shrink-0 items-center gap-2 max-md:order-[-1]">
          {/*
            The clock time it was sent, not how long ago.

            "Fri" and "1h" answer a question nobody asks of a thread they are
            reading: what a reader wants is to line the messages up against the
            rest of the day -- against the call they remember taking, or
            against a customer's "I rang at half one and nobody had replied".
            A relative stamp cannot answer that, and it goes stale while the
            thread sits open over lunch. The audit lines between these bubbles
            have said the wall-clock time for a while; this makes the messages
            agree with them.

            The relative form stays in the tooltip, because "how long has this
            been sitting" is a real second question and cheap to keep.
          */}
          <time
            dateTime={message.sentAt}
            title={`${absoluteTime(message.sentAt)} · ${relativeTime(message.sentAt)}`}
            className="text-[11px] text-faint"
          >
            {dayAndTime(message.sentAt)}
          </time>

          {canEdit && !editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded px-1 text-[11px] text-amber-700 hover:bg-amber-200/60 dark:text-amber-300 dark:hover:bg-amber-800/60"
            >
              Edit
            </button>
          ) : null}

          <MessageActions
            message={message}
            onAct={(kind) => onAct(kind, message)}
            onCopyLink={() => onCopyLink(message.id)}
          />
        </span>
      </header>

      {/* Phone only: the desktop has the width to say who a forward went to
          in its badge, and the panel beside the thread lists everybody. */}
      {isDesktop ? null : (
        <SenderDetails
          message={message}
          open={showDetails}
          onToggle={() => setShowDetails((value) => !value)}
        />
      )}

      {editing ? (
        <NoteEditor
          ticketId={ticketId}
          message={message}
          onDone={() => setEditing(false)}
        />
      ) : null}

      {editing ? null : message.bodyHtml ? (
        /*
         * The HTML is sanitised server-side (see api common/sanitize.ts) before
         * it reaches this component. Rendering raw email HTML from strangers in
         * an authenticated session is the highest-value XSS target here, so the
         * sanitiser is not optional and must not be bypassed by rendering
         * message.bodyHtml from any other source.
         */
        <>
          <div
            ref={body}
            /*
             * `mail-colours` turns real mail's own colours over in dark mode
             * (see index.css). The HTML is written by the sender against a
             * white background they assumed -- black body text, a table with
             * white cells -- and none of that was legible on a dark card,
             * while a white sheet inside it never looked like the app. Notes
             * are excluded: they are written here, in the app's own colours,
             * and they should keep them.
             */
            className={clsx("email-body", !isNote && "mail-colours")}
            dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
          />

          {/*
            The thread this reply was written on top of, already cut off by the
            API. Folded away because the conversation above is that same thread,
            message by message -- opening it is for the day the cut looks wrong,
            or the sender edited what they quoted.

            Outside the mail rather than inside it. The mail's colours are
            turned over in dark mode and the disclosure is ours: inside, the
            button would be inverted along with the sender's ink.
          */}
          {message.quotedHtml ? (
            <QuoteDisclosure
              open={showQuoted}
              onToggle={() => setShowQuoted((value) => !value)}
            >
              <div className="mt-2 border-l-2 border-line pl-3">
                <div
                  ref={quotedBody}
                  className={clsx("email-body", !isNote && "mail-colours")}
                  dangerouslySetInnerHTML={{ __html: message.quotedHtml }}
                />
              </div>
            </QuoteDisclosure>
          ) : null}
        </>
      ) : bodySpokenFor ? null : (
        <div className="email-body whitespace-pre-wrap">
          {visible}
          {quoted ? (
            <QuoteDisclosure
              open={showQuoted}
              onToggle={() => setShowQuoted((value) => !value)}
            >
              <div className="mt-2 border-l-2 border-line pl-3 text-muted">
                {quoted}
              </div>
            </QuoteDisclosure>
          ) : null}
        </div>
      )}

      {editing ? null : <InlineImages images={inlineImages} />}

      {editing || !message.location ? null : (
        <LocationCard messageId={message.id} location={message.location} />
      )}

      {/*
        The listing, on the buyer's messages only: every message in an eBay
        conversation carries it, and a link under each of the desk's own
        replies would repeat what the buyer's message above already says.
        Digits only, because it becomes part of a URL.
      */}
      {editing ||
      message.kind !== "inbound" ||
      !message.ebayItemId ||
      !/^\d+$/.test(message.ebayItemId) ? null : (
        <a
          href={`https://www.ebay.co.uk/itm/${message.ebayItemId}`}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1 inline-block text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          About eBay item {message.ebayItemId} ↗
        </a>
      )}

      {editing ? null : (
        <Attachments message={message} onPreview={setPreview} />
      )}

      {preview ? (
        <AttachmentPreview file={preview} onClose={() => setPreview(null)} />
      ) : null}
    </article>
  );
}

/**
 * The fold over a message's quoted history.
 *
 * One component for both bodies -- the HTML the API split, and the plain text
 * `splitQuotedText` splits below. Whether a message arrived as HTML is not
 * something an agent knows or should have to, so the two must not drift into
 * differently-worded buttons that behave differently.
 *
 * A button rather than `<details>`: the summary has to sit under the message
 * it belongs to and read as chrome, and a native disclosure brings a marker
 * and a layout that fight both.
 */
function QuoteDisclosure({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        /* Taller on a phone, where it is a thumb rather than a pointer that
           has to find it. */
        className="mt-2 block rounded bg-surface-2 px-2 py-0.5 text-xs text-muted-on-tint hover:bg-line hover:text-content max-md:px-3 max-md:py-1.5"
      >
        {open ? "Hide quoted text" : "··· Show quoted text"}
      </button>
      {open ? children : null}
    </>
  );
}

/**
 * Rewriting a note in place.
 *
 * A plain textarea rather than the rich editor the composer uses. This is for
 * the typo and the wrong order number -- the two things the five-minute window
 * exists for -- and swapping in a second contenteditable surface, with its own
 * canned responses and mention handling, would mean an edit could quietly
 * introduce a mention that the server deliberately does not act on.
 *
 * Existing markup is preserved: the textarea is seeded with the note's HTML so
 * a note that arrived with formatting keeps it unless the agent removes it.
 */
function NoteEditor({
  ticketId,
  message,
  onDone,
}: {
  ticketId: number;
  message: Message;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(message.bodyHtml ?? message.bodyText ?? "");

  const save = useMutation({
    mutationFn: () => api.editNote(ticketId, message.id, draft.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticket(ticketId) });
      onDone();
    },
  });

  const error = save.error instanceof Error ? save.error.message : null;

  return (
    <div>
      <textarea
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onDone();
          }
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (draft.trim()) save.mutate();
          }
        }}
        rows={4}
        className="w-full rounded-md border border-amber-300 bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-amber-500 dark:border-amber-700"
      />

      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}

      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!draft.trim() || save.isPending}
          className="rounded-md bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-40"
        >
          {save.isPending ? "Saving\u2026" : "Save"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-md px-2 py-1 text-xs text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400"
        >
          Cancel
        </button>
        <span className="text-[11px] text-faint">
          Colleagues will see that this note was edited.
        </span>
      </div>
    </div>
  );
}

/**
 * The files on a message.
 *
 * A picture is shown as the picture. It used to be a row of filenames, which
 * meant a customer sending a screenshot of the fault -- far and away the
 * commonest attachment on this desk -- produced "Screenshot 2026-08-28 at
 * 14.32.23.png" and a download the agent had to take before they could see what
 * they had been sent. The name of a screenshot says nothing about it; the
 * screenshot says all of it.
 *
 * Everything else stays a chip, because there is nothing to show. Both open the
 * viewer rather than downloading on click: opening is what somebody wants nine
 * times out of ten, and the viewer offers the download as its second button.
 *
 * There is no longer a line reading "attachments are embedded in the message
 * above". It existed for the message whose only attachment was a signature
 * logo, where the file list came out empty -- and it pointed at a message that
 * was visibly not showing them, because nothing resolved the `cid:` reference
 * the logo was behind. Both halves of that are fixed: the reference is resolved
 * as the mail is read, and a file that cannot be drawn is listed here instead
 * of being dropped. Every attachment now appears somewhere, so a sentence
 * apologising for the ones that did not has nothing left to describe.
 */
function Attachments({
  message,
  onPreview,
}: {
  message: Message;
  onPreview: (file: PreviewFile) => void;
}) {
  if (message.attachments.length === 0) return null;

  const asFile = (attachment: MessageAttachment): PreviewFile => ({
    name: attachment.filename,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
    inlineUrl: canPreview(attachment.mimeType)
      ? `/api/attachments/${attachment.id}/inline`
      : null,
    downloadUrl: `/api/attachments/${attachment.id}`,
  });

  const pictures = message.attachments.filter((file) => isImageType(file.mimeType));
  const files = message.attachments.filter((file) => !isImageType(file.mimeType));

  return (
    <>
      {pictures.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {pictures.map((attachment) => (
            <li key={attachment.id}>
              <button
                type="button"
                onClick={() => onPreview(asFile(attachment))}
                title={`${attachment.filename} (${attachment.mimeType})`}
                className="block overflow-hidden rounded-md border border-line transition-colors hover:border-brand-500"
              >
                {/*
                  Bounded to about the height of a paragraph. A phone photo is
                  4000px tall, and a bubble taller than the reply above it turns
                  the conversation into a scroll -- which is the failure the
                  filename version at least did not have. Big enough to read a
                  screenshot's shape and decide to open it, no bigger.
                */}
                <img
                  src={`/api/attachments/${attachment.id}/inline`}
                  alt={attachment.filename}
                  loading="lazy"
                  className="max-h-48 max-w-[16rem] object-contain"
                />
                <span className="block max-w-[16rem] truncate border-t border-line bg-surface-2 px-2 py-1 text-left text-[11px] text-muted">
                  {attachment.filename}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {files.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {files.map((attachment) => (
            <li key={attachment.id}>
              <button
                type="button"
                onClick={() => onPreview(asFile(attachment))}
                title={`${attachment.filename} (${attachment.mimeType})`}
                className="inline-flex max-w-xs items-center gap-1.5 rounded-md border border-line px-2 py-1 text-xs text-content transition-colors hover:bg-surface-2"
              >
                <span aria-hidden>📎</span>
                <span className="truncate">{attachment.filename}</span>
                {attachment.sizeBytes ? (
                  <span className="shrink-0 text-muted">
                    {formatBytes(attachment.sizeBytes)}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * Pictures that are the message rather than files beside it.
 *
 * Bounded rather than shown at their own size. A sticker arrives as a 512px
 * square, and a bubble whose picture is taller than the reply above it turns a
 * conversation into a scroll -- so it is capped at roughly the height of three
 * lines of text, which is what a sticker is worth.
 *
 * Served from `/inline` rather than the download route the file list uses. That
 * route forces a download on everything, deliberately; this one serves a short
 * list of image types and nothing else. See the API's inline-images.ts.
 */
function InlineImages({ images }: { images: MessageAttachment[] }) {
  if (images.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {images.map((image) => (
        <img
          key={image.id}
          src={`/api/attachments/${image.id}/inline`}
          /* The filename, which for a sticker is "sticker.webp" -- not much,
             but it is what a screen reader can truthfully say about a picture
             nobody has described. */
          alt={image.filename}
          loading="lazy"
          className="max-h-40 max-w-[12rem] rounded-md object-contain"
        />
      ))}
    </div>
  );
}

/**
 * A place the customer shared.
 *
 * The map is a picture the API fetches from Google and mirrors, so the key
 * stays on the server -- see the API's maps module. The SPA builds the URL
 * rather than being told whether a map exists, because whether one does is a
 * question about a key on the server and plumbing a flag through the ticket
 * payload to answer it would be a lot of machinery for a picture. A desk with
 * no key gets a 404, the image hides itself, and what is left is the card:
 * where they said they were, and a link to open it properly.
 *
 * Which is why the text is not a caption on the map. It is the answer, and the
 * map is the illustration.
 */
function LocationCard({
  messageId,
  location,
}: {
  messageId: number;
  location: MessageLocation;
}) {
  const [mapFailed, setMapFailed] = useState(false);

  const place = location.name?.trim() || null;
  const address = location.address?.trim() || null;
  const coordinates = `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;

  return (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${location.latitude},${location.longitude}`,
      )}`}
      target="_blank"
      rel="noreferrer"
      title="Open in Google Maps — opens in a new tab"
      className="mt-2 block max-w-sm overflow-hidden rounded-md border border-line transition-colors hover:border-brand-500"
    >
      {mapFailed ? null : (
        <img
          src={`/api/messages/${messageId}/map`}
          alt={`Map of ${place ?? address ?? coordinates}`}
          loading="lazy"
          onError={() => setMapFailed(true)}
          className="block w-full"
        />
      )}

      <div className="px-2 py-1.5 text-xs">
        <span aria-hidden>📍</span>{" "}
        <span className="font-medium text-content">
          {place ?? address ?? "Shared location"}
        </span>
        {place && address ? (
          <span className="block text-muted">{address}</span>
        ) : null}
        <span className="block text-faint">{coordinates}</span>
      </div>
    </a>
  );
}

/**
 * Human-readable size. Binary units, one decimal below 10 -- "1.4 MB" carries
 * everything an agent needs to decide whether to open a file, and "1,468,006
 * bytes" carries the same information in a form nobody reads.
 */
function formatBytes(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const rounded = value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value);
  return `${rounded} ${units[unit]}`;
}

/**
 * Split a plain-text body into the new content and the quoted trailer.
 *
 * Long support threads are mostly quoted history; showing all of it on every
 * message makes a conversation unreadable. Only used for text-only mail --
 * HTML bodies are collapsed by CSS on blockquote instead.
 */
function splitQuotedText(body: string | null): {
  visible: string;
  quoted: string | null;
} {
  if (!body) return { visible: "", quoted: null };

  const lines = body.split("\n");

  const markers = [
    /^-{2,}\s*Original Message\s*-{2,}/i,
    /^On .+ wrote:$/i,
    /^From:\s/i,
    /^_{5,}$/,
  ];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]!.trim();
    if (markers.some((marker) => marker.test(line))) {
      // Require some content above the marker; otherwise a reply that opens
      // with the quote would render as entirely empty.
      const above = lines.slice(0, index).join("\n").trim();
      if (above) {
        return { visible: above, quoted: lines.slice(index).join("\n") };
      }
    }
  }

  return { visible: body, quoted: null };
}

/**
 * A message folded to one line, for the phone layout.
 *
 * What Gmail's app does with every message but the newest, and for the same
 * reason: on a handset the thread is read from the bottom, and the earlier
 * messages are there to be recognised -- who, when, roughly what -- rather
 * than re-read. A row each lets somebody see the shape of a conversation on
 * one screen and open the one they want.
 *
 * Keeps the bubble's colours, so a folded internal note is still visibly not
 * something the customer saw, and keeps the anchor id, so a link to this
 * message still has somewhere to land while the thread opens it.
 */
function CollapsedMessage({
  message,
  author,
  summary,
  highlighted,
  onExpand,
}: {
  message: Message;
  author: string;
  summary: string;
  highlighted: boolean;
  onExpand?: () => void;
}) {
  const isNote = message.kind === "note";
  const isOutbound = message.kind === "outbound";

  return (
    <article
      id={messageAnchor(message.id)}
      className={clsx(
        "scroll-mt-4 border-b",
        isNote
          ? "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40"
          : isOutbound
            ? "border-line bg-surface-2/60"
            : "border-line bg-surface",
        highlighted && "ring-2 ring-inset ring-brand-500",
      )}
    >
      <button
        type="button"
        onClick={onExpand}
        aria-expanded={false}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left active:bg-surface-2"
      >
        <SenderAvatar message={message} author={author} />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 truncate text-[15px] font-semibold text-content">
              {author}
            </span>
            {isNote ? (
              <span className="shrink-0 rounded bg-amber-200 px-1 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                Note
              </span>
            ) : null}
            {message.delivery === "failed" ? (
              <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] text-red-700 dark:bg-red-950 dark:text-red-300">
                Failed
              </span>
            ) : null}
            <time
              dateTime={message.sentAt}
              className="ml-auto shrink-0 text-xs text-faint"
            >
              {dayAndTime(message.sentAt)}
            </time>
          </span>
          <span className="block truncate text-sm text-muted">
            {message.attachments.length > 0 ? (
              <span aria-label="Has attachments" className="mr-1">📎</span>
            ) : null}
            {summary}
          </span>
        </span>
      </button>
    </article>
  );
}

/**
 * The one line a folded message shows.
 *
 * The API's snippet where there is one -- it is cut from the mail with the
 * quoted history already removed -- and otherwise the visible half of the
 * plain text, which is what a note or a WhatsApp message has. Whitespace is
 * squashed because a line break in a one-line summary is just a lost word.
 */
function summaryLine(message: Message, visible: string): string {
  const text = (message.snippet || visible || "").replace(/\s+/g, " ").trim();
  if (text) return text;
  const file = message.attachments[0];
  return file ? file.filename : "";
}

/**
 * A face for whoever wrote the message, on the phone layout.
 *
 * The agent's own circle when a colleague wrote it, so it matches the one in
 * the queue. Anybody else is tinted from a hash of their address: stable for
 * a sender across a thread, which is what lets a reader tell two customers on
 * one ticket apart at a glance, and never mistaken for an agent's colour on
 * purpose -- it is simply the same palette.
 */
function SenderAvatar({
  message,
  author,
  className,
}: {
  message: Message;
  author: string;
  className?: string;
}) {
  const id =
    message.authorUserId ??
    hashOf(message.fromEmail ?? message.fromPhone ?? author);

  return <Avatar size="md" agent={{ id, name: author }} className={className} />;
}

function hashOf(text: string): number {
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return hash;
}

/**
 * "to Sam, help@ ▾", and the full headers behind it.
 *
 * Gmail's app line, under the sender. Folded because it is rarely the point,
 * and there because when it is the point -- "was accounts Cc'd on this one?"
 * -- the phone otherwise has no way to answer it short of the details sheet,
 * which lists the ticket's people rather than this message's.
 */
function SenderDetails({
  message,
  open,
  onToggle,
}: {
  message: Message;
  open: boolean;
  onToggle: () => void;
}) {
  const to = message.toRecipients ?? [];
  const cc = message.ccRecipients ?? [];
  const from = message.fromEmail ?? message.fromPhone;

  if (to.length === 0 && cc.length === 0 && !from) return null;

  const names = [...to, ...cc].map((person) => person.name || person.email);
  const summary =
    names.length === 0
      ? "details"
      : `to ${names.slice(0, 2).join(", ")}${
          names.length > 2 ? ` and ${names.length - 2} more` : ""
        }`;

  return (
    <div className="-mt-1 mb-2 pl-[2.625rem] text-xs text-muted">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex max-w-full items-center gap-1 py-1 text-left"
      >
        <span className="truncate">{summary}</span>
        <span aria-hidden className="shrink-0">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open ? (
        <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 break-words">
          {from ? (
            <>
              <dt className="text-faint">From</dt>
              <dd>
                {message.fromName ? `${message.fromName} <${from}>` : from}
              </dd>
            </>
          ) : null}
          {to.length > 0 ? (
            <>
              <dt className="text-faint">To</dt>
              <dd>{addresses(to)}</dd>
            </>
          ) : null}
          {cc.length > 0 ? (
            <>
              <dt className="text-faint">Cc</dt>
              <dd>{addresses(cc)}</dd>
            </>
          ) : null}
          <dt className="text-faint">Date</dt>
          <dd>{absoluteTime(message.sentAt)}</dd>
        </dl>
      ) : null}
    </div>
  );
}

function addresses(people: EmailAddress[]): string {
  return people
    .map((person) => (person.name ? `${person.name} <${person.email}>` : person.email))
    .join(", ");
}

/**
 * The smallest a mail is scaled to before it is left to scroll instead.
 *
 * Below about half size the text in a typical 600px newsletter is under 8px,
 * which is not reading -- at that point a sideways scroll inside the message
 * is the lesser evil, and `.email-body` still provides one.
 */
const MIN_FIT = 0.5;

/**
 * Scale an email down to the width of the screen when it will not reflow.
 *
 * What Gmail's app does with a mail built for a 600px desktop pane. Most mail
 * reflows on its own once the stylesheet stops forcing it (see `.ticket-thread`
 * in index.css), and this does nothing to that. What is left is the table
 * with fixed-width cells, the banner image in a cell with a width attribute:
 * mail whose author has insisted on a width. Shown at that width it scrolls
 * sideways inside a thread that scrolls down, and a phone cannot do both
 * comfortably -- so the whole message is zoomed until it fits, and the
 * browser's own pinch zoom is there for the fine print.
 *
 * CSS `zoom` rather than a transform: a transform scales the paint and leaves
 * the layout box at full size, so the bubble would keep the mail's original
 * height with a gap under it. Zoom scales the layout too.
 *
 * Measured at zoom 1 every time, so the result never depends on the last
 * one, and again when the width changes -- a phone turned sideways -- or a
 * picture inside it loads and changes the mail's shape.
 */
function useFitToWidth(
  ref: RefObject<HTMLDivElement | null>,
  enabled: boolean,
  html: string | null,
): void {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!enabled) {
      element.style.zoom = "";
      return;
    }

    const fit = (): void => {
      element.style.zoom = "";
      const room = element.clientWidth;
      const needed = element.scrollWidth;
      if (room > 0 && needed > room + 1) {
        element.style.zoom = String(Math.max(room / needed, MIN_FIT));
      }
    };

    fit();

    /* The host, not the element: zooming the element changes its own box,
       and an observer on that would answer every fit with another. */
    const host = element.parentElement ?? element;
    let width = host.clientWidth;
    const observer = new ResizeObserver(() => {
      if (host.clientWidth === width) return;
      width = host.clientWidth;
      fit();
    });
    observer.observe(host);

    /* `load` does not bubble, so it is caught on the way down instead. */
    element.addEventListener("load", fit, true);

    return () => {
      observer.disconnect();
      element.removeEventListener("load", fit, true);
      element.style.zoom = "";
    };
  }, [ref, enabled, html]);
}
